import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeaderSearch from '../HeaderSearch/HeaderSearch';
import DonateModal from '../DonateModal/DonateModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSoundNotificationsContext } from '../../contexts/SoundNotificationsContext';
import { trackFeatureUsage, trackLanguageChange } from '../../utils/analytics';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useInterfacePreference } from '../../hooks/useInterfacePreference';
import { MAIN_NAV_ITEMS, MORE_NAV_ITEMS } from '../../config/navItems';
import './Header.css';

// Standalone PRO button is hidden per client request (2026-05-31); the
// Basic/Pro switch now lives in the "Whales" nav dropdown. Flip to true to
// bring the standalone button back.
const SHOW_PRO_BUTTON = false;

function Header({ isConnected, isSubscribed, subscriptionError, onStartTour }) {
  const { t } = useTranslation();
  const { language, setLanguage, localizedPath } = useLanguage();
  const { setPreference } = useInterfacePreference();
  const { volume, setVolumeLevel } = useSoundNotificationsContext();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDonateOpen, setIsDonateOpen] = useState(false);
  const [isWhalesDropdownOpen, setIsWhalesDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const settingsRef = useRef(null);
  const whalesRef = useRef(null);
  const navListRef = useRef(null);
  const collapseRef = useRef(null);
  const togglerRef = useRef(null);
  const showAiTrader = useFeatureFlag('show-ai-trader-nav', true);

  // Determine if we should show live indicator
  const isLive = isConnected && isSubscribed && !subscriptionError;


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMoreDropdownOpen(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsSettingsOpen(false);
      }
      if (whalesRef.current && !whalesRef.current.contains(event.target)) {
        setIsWhalesDropdownOpen(false);
      }
      // Close the mobile hamburger menu when tapping anything outside it
      // (e.g. the settings gear or coin search), not just nav links.
      if (
        collapseRef.current && !collapseRef.current.contains(event.target) &&
        togglerRef.current && !togglerRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // macOS-dock proximity effect on the desktop nav: items near the cursor
  // scale up and brighten by distance, so neighbours react before hover.
  // Drives a `--prox` (0..1) CSS var per link; the styling lives in Header.css.
  // Disabled on touch / mobile / reduced-motion (var stays 0 → no transform).
  useEffect(() => {
    const nav = navListRef.current;
    if (!nav || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mqDesktop = window.matchMedia('(min-width: 992px)');
    const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const enabled = () => mqDesktop.matches && mqHover.matches && !mqMotion.matches;
    const FALLOFF = 200; // px — proximity reach; wide enough to ripple across 2-3 links

    let raf = 0;
    let clientX = null;
    const reset = () => nav.querySelectorAll('.nav-link').forEach((el) => el.style.removeProperty('--prox'));
    const apply = () => {
      raf = 0;
      if (clientX == null) return;
      nav.querySelectorAll('.nav-link').forEach((el) => {
        const r = el.getBoundingClientRect();
        const t = Math.max(0, 1 - Math.abs(clientX - (r.left + r.width / 2)) / FALLOFF);
        el.style.setProperty('--prox', t.toFixed(3));
      });
    };
    const onMove = (e) => {
      if (!enabled()) { if (clientX != null) { clientX = null; reset(); } return; }
      clientX = e.clientX;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      clientX = null;
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      reset();
    };

    nav.addEventListener('pointermove', onMove);
    nav.addEventListener('pointerleave', onLeave);
    return () => {
      nav.removeEventListener('pointermove', onMove);
      nav.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Check if current path matches route (accounting for language prefix)
  const isActiveRoute = (route) => {
    const path = location.pathname;
    const cleanPath = path.replace(/^\/(en|ru|ko|zh)/, '') || '/trades';
    return cleanPath === route || cleanPath.startsWith(route + '/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMoreDropdown = () => {
    setIsMoreDropdownOpen(!isMoreDropdownOpen);
  };

  const closeMoreDropdown = () => {
    setIsMoreDropdownOpen(false);
  };

  const toggleWhalesDropdown = () => {
    setIsWhalesDropdownOpen((prev) => !prev);
  };

  const closeWhalesDropdown = () => {
    setIsWhalesDropdownOpen(false);
  };

  // Whether an item should render given its optional feature flag.
  // Only AI Trader is gated today (show-ai-trader-nav); preserve its default-true.
  const isNavItemVisible = (item) => !item.featureFlag || showAiTrader;

  // Check if any "More" dropdown route is active
  const isMoreActive = () => MORE_NAV_ITEMS.some((item) => isActiveRoute(item.path));

  return (
    <header className="app-header">
      <nav className="navbar navbar-expand-xl">
        <div className="container-fluid">
          <div className="header-left">
            <a className="navbar-brand" href="/">
              <img src="/assets_landing/img/trace.svg" alt="Trace" style={{ height: '32px' }} />
            </a>

            {/* Support / Donate — subtle, left of the center nav */}
            <button
              type="button"
              className="nav-donate-btn"
              onClick={() => {
                trackFeatureUsage('donate_modal_open', { page: location.pathname });
                setIsDonateOpen(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              <span className="nav-donate-label">{t('nav.support', 'Support TraderMap')}</span>
            </button>
          </div>

          <button
            className="navbar-toggler"
            type="button"
            ref={togglerRef}
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`navbar-collapse ${isMobileMenuOpen ? 'show' : ''}`} id="navbarNav" ref={collapseRef}>
            <ul className="navbar-nav" ref={navListRef}>
              {/* Whales — Basic/Pro switch dropdown (mirrors the terminal header).
                  Replaces the standalone PRO button as the way into the terminal. */}
              <li className="nav-item nav-dropdown" ref={whalesRef}>
                <button
                  className={`nav-link nav-dropdown-toggle ${isActiveRoute('/trades') ? 'active' : ''}`}
                  onClick={toggleWhalesDropdown}
                  aria-expanded={isWhalesDropdownOpen}
                >
                  {t('nav.perpetualPosition', 'Whales')}
                  <svg className={`dropdown-arrow ${isWhalesDropdownOpen ? 'open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                <div className={`nav-dropdown-menu ${isWhalesDropdownOpen ? 'show' : ''}`}>
                  <Link
                    className="nav-dropdown-item active"
                    to={localizedPath('/trades')}
                    onClick={() => { setPreference('basic'); closeWhalesDropdown(); closeMobileMenu(); }}
                  >
                    Basic <span className="nav-mode-badge">STANDARD</span>
                  </Link>
                  <Link
                    className="nav-dropdown-item"
                    to={localizedPath('/terminal')}
                    onClick={() => { setPreference('pro'); closeWhalesDropdown(); closeMobileMenu(); }}
                  >
                    Professional <span className="nav-mode-badge nav-mode-badge--terminal">TERMINAL</span>
                  </Link>
                </div>
              </li>

              {MAIN_NAV_ITEMS.filter((i) => i.key !== 'trades').filter(isNavItemVisible).map((item) => (
                <li className="nav-item" key={item.key}>
                  <Link
                    className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                    to={localizedPath(item.path)}
                    onClick={closeMobileMenu}
                  >
                    {t(item.i18nKey, item.fallback)}
                    {item.badge && <span className="nav-badge-sup">{item.badge}</span>}
                  </Link>
                </li>
              ))}
              <li className="nav-item nav-dropdown" ref={dropdownRef}>
                <button
                  className={`nav-link nav-dropdown-toggle ${isMoreActive() ? 'active' : ''}`}
                  onClick={toggleMoreDropdown}
                  aria-expanded={isMoreDropdownOpen}
                >
                  {t('nav.more', 'More')}
                  <svg className={`dropdown-arrow ${isMoreDropdownOpen ? 'open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                <div className={`nav-dropdown-menu ${isMoreDropdownOpen ? 'show' : ''}`}>
                  {MORE_NAV_ITEMS.filter(isNavItemVisible).map((item) => (
                    <Link
                      key={item.key}
                      className={`nav-dropdown-item ${isActiveRoute(item.path) ? 'active' : ''}`}
                      to={localizedPath(item.path)}
                      onClick={() => { closeMobileMenu(); closeMoreDropdown(); }}
                    >
                      {t(item.i18nKey, item.fallback)}
                      {item.badge && <span className="nav-badge-sup">{item.badge}</span>}
                    </Link>
                  ))}
                </div>
              </li>

              {/* Mobile-only: Telegram alerts + Support, pinned at the bottom of
                  the hamburger menu (under "More"). Hidden on desktop, where
                  these live in the header bar instead. */}
              <li className="nav-item nav-mobile-cta">
                <a
                  className="nav-tg-btn"
                  href="https://t.me/tradermap_io"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackFeatureUsage('whale_alerts_telegram_click', { page: location.pathname, src: 'mobile_menu' });
                    closeMobileMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
                  </svg>
                  <span className="nav-tg-label">{t('nav.whaleAlerts', 'Whale Alerts')}</span>
                  <span className="nav-tg-new">NEW</span>
                </a>
                <button
                  type="button"
                  className="nav-donate-btn"
                  onClick={() => {
                    trackFeatureUsage('donate_modal_open', { page: location.pathname, src: 'mobile_menu' });
                    setIsDonateOpen(true);
                    closeMobileMenu();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  <span className="nav-donate-label">{t('nav.support', 'Support TraderMap')}</span>
                </button>
              </li>
            </ul>
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* Coin Search */}
            <HeaderSearch />

            {/* Whale Alerts — Telegram channel */}
            <a
              className="nav-tg-btn"
              href="https://t.me/tradermap_io"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackFeatureUsage('whale_alerts_telegram_click', { page: location.pathname });
                closeMobileMenu();
              }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
              </svg>
              <span className="nav-tg-label">{t('nav.whaleAlerts', 'Whale Alerts')}</span>
              <span className="nav-tg-new">NEW</span>
            </a>

            {/* Pro Version (hidden — see SHOW_PRO_BUTTON; switch lives in Whales dropdown) */}
            {SHOW_PRO_BUTTON && (
              <Link className="nav-pro-btn" to={localizedPath('/terminal')} onClick={closeMobileMenu}>
                PRO
              </Link>
            )}

            {/* Settings (Sound + Language + Tour) */}
            <div className="settings-dropdown-wrapper" ref={settingsRef}>
              <button
                className={`settings-btn ${isSettingsOpen ? 'active' : ''}`}
                onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                aria-label="Settings"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M8.686 4l2.607-2.607a1 1 0 011.414 0L15.314 4H19a1 1 0 011 1v3.686l2.607 2.607a1 1 0 010 1.414L20 15.314V19a1 1 0 01-1 1h-3.686l-2.607 2.607a1 1 0 01-1.414 0L8.686 20H5a1 1 0 01-1-1v-3.686l-2.607-2.607a1 1 0 010-1.414L4 8.686V5a1 1 0 011-1h3.686zM6 6v3.515L3.515 12 6 14.485V18h3.515L12 20.485 14.485 18H18v-3.515L20.485 12 18 9.515V6h-3.515L12 3.515 9.515 6H6zm6 10a4 4 0 110-8 4 4 0 010 8zm0-2a2 2 0 100-4 2 2 0 000 4z"/>
                </svg>
              </button>

              {isSettingsOpen && (
                <div className="settings-panel">
                  {/* Volume */}
                  <div className="settings-section">
                    <div className="settings-section-label">{t('settings.sound', 'Sound')}</div>
                    <div className="settings-volume-row">
                      <button
                        className="settings-volume-icon"
                        onClick={() => setVolumeLevel(volume > 0 ? 0 : 70)}
                        title={volume === 0 ? 'Unmute' : 'Mute'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          {volume === 0 ? (
                            <path d="M5.889 16H2a1 1 0 01-1-1V9a1 1 0 011-1h3.889l5.294-4.332a.5.5 0 01.817.387v15.89a.5.5 0 01-.817.387L5.89 16zm14.525-4l3.536 3.536-1.414 1.414L19 13.414l-3.536 3.536-1.414-1.414L17.586 12l-3.536-3.536 1.414-1.414L19 10.586l3.536-3.536 1.414 1.414L20.414 12z"/>
                          ) : (
                            <path d="M2 16h3.889l5.294 4.332a.5.5 0 00.817-.387V4.055a.5.5 0 00-.817-.387L5.89 8H2a1 1 0 00-1 1v6a1 1 0 001 1zm21-4a9 9 0 01-3.738 7.262l-1.417-1.418A7 7 0 0021 12a7 7 0 00-3.156-5.844l1.418-1.418A9 9 0 0123 12zm-5 0a5 5 0 01-2.287 4.213l-1.429-1.429A3 3 0 0016 12a3 3 0 00-1.716-2.784l1.43-1.429A5 5 0 0118 12z"/>
                          )}
                        </svg>
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume}
                        onChange={(e) => setVolumeLevel(Number(e.target.value))}
                        className="settings-volume-slider"
                      />
                      <span className="settings-volume-value">{volume}</span>
                    </div>
                  </div>

                  {/* Language */}
                  <div className="settings-section">
                    <div className="settings-section-label">{t('settings.language', 'Language')}</div>
                    <div className="settings-lang-row">
                      {[
                        { code: 'en', label: 'EN' },
                        { code: 'ru', label: 'RU' },
                        { code: 'ko', label: '한국어' },
                        { code: 'zh', label: '中文' }
                      ].map((lang) => (
                        <button
                          key={lang.code}
                          className={`settings-lang-btn ${language === lang.code ? 'active' : ''}`}
                          onClick={() => {
                            if (lang.code !== language) trackLanguageChange(language, lang.code);
                            setLanguage(lang.code);
                          }}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Help / Tour */}
                  <div className="settings-section" data-tour="header-help-button">
                    <button
                      className="settings-tour-btn"
                      onClick={() => {
                        setIsSettingsOpen(false);
                        trackFeatureUsage('help_button_clicked', { page: location.pathname, has_tour: !!onStartTour });
                        if (onStartTour) onStartTour();
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                      </svg>
                      {t('settings.pageTour', 'Page Tour')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Live Indicator */}
            <div className={`header-live ${isLive ? 'active' : ''}`} data-tour="header-live-indicator">
              <span className="live-dot"></span>
              <span className="live-text">Live</span>
            </div>
          </div>
        </div>
      </nav>

      <DonateModal open={isDonateOpen} onClose={() => setIsDonateOpen(false)} />
    </header>
  );
}

export default Header;
